<template>
  <div class="tenant-selector">
    <FormField 
      description="Select a tenant to view and edit configuration"
    >
      <SelectBox
        v-model="selectedValue"
        :options="tenantOptions"
        placeholder="Select tenant..."
        :loading="loading"
        :disabled="isDisabled"
        @update:model-value="handleTenantChange"
      />
    </FormField>
    
    <div v-if="error" class="tenant-selector__error">
      <Icon name="error" />
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import FormField from '@/components/molecules/FormField.vue'
import SelectBox from '@/components/atoms/SelectBox.vue'
import Icon from '@/components/atoms/Icon.vue'
import { configurationService } from '@/services/api'
import { withApiNotifications, API_MESSAGES } from '@/utils/api-notifications'
import type { Tenant } from '@/types/configuration'
import { useConfigurationStore } from '@/stores/configuration'

interface TenantOption {
  value: string
  label: string
  description?: string
}

interface Props {
  modelValue?: string
  disabled?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: string | undefined): void
  (e: 'change', value: string | undefined): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  disabled: false
})

const emit = defineEmits<Emits>()

// Store
const configStore = useConfigurationStore()

// State
const loading = ref(false)
const error = ref<string | null>(null)
const tenants = ref<TenantOption[]>([])

// Computed
const selectedValue = computed({
  get: () => props.modelValue || '',
  set: (value) => emit('update:modelValue', value || undefined)
})

const tenantOptions = computed(() => tenants.value)

// Disable tenant selection when in edit mode or explicitly disabled via prop
const isDisabled = computed(() => props.disabled || configStore.isEditMode)

// Methods
const handleTenantChange = (value: string | number) => {
  const stringValue = value.toString()
  emit('change', stringValue || undefined)
}

const loadTenants = async () => {
  loading.value = true
  error.value = null
  
  try {
    await withApiNotifications(
      async () => {
        const tenantsData = await configurationService.getTenants()
        tenants.value = tenantsData.map((tenant: Tenant) => ({
          value: tenant.name,
          label: tenant.displayName,
          description: tenant.description || `Tenant ${tenant.displayName}`
        }))
        return tenantsData
      },
      {
        loadingMessage: API_MESSAGES.LOADING.GET_TENANTS,
        successMessage: API_MESSAGES.SUCCESS.GET_TENANTS,
        errorMessage: API_MESSAGES.ERROR.GET_TENANTS,
        showSuccess: false // Don't show success for routine loading
      }
    )
  } catch (err: any) {
    error.value = err.message || 'Failed to load tenants'
    console.error('Tenant loading error:', err)
    // No fallback data - show error state instead
    tenants.value = []
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadTenants()
})

// Expose methods to parent
defineExpose({
  loadTenants
})
</script>

<style scoped lang="scss">
.tenant-selector {
  &__error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 12px;
    background-color: var(--vscode-inputValidation-errorBackground);
    border: 1px solid var(--vscode-inputValidation-errorBorder);
    border-radius: 4px;
    color: var(--vscode-inputValidation-errorForeground);
    font-size: 12px;
  }
}
</style>
